import { ActionIcon, Tooltip, UnstyledButton } from "@mantine/core";
import { X } from "lucide-react";
import type { ReactElement, ReactNode } from "react";

import classes from "./SourceTool.module.css";

/** What a tool holds, as it shows it: the picture, when there is one, and its name. */
export interface SourceToolView {
  url: string | null;
  name: string;
}

export interface SourceToolProps {
  /** Live's tooltip, and the button's name: "Products", "Reference images". */
  label: string;
  /** Live's tooltip where it is not the name (a template form's own words). */
  tip?: string;
  icon: ReactNode;
  /** The source this tool chose, or null. */
  view: SourceToolView | null;
  /** The x badge's name: "Remove the product". */
  removeLabel: string;
  onRemove: () => void;
  onClick?: () => void;
  /** Wraps the button, as a popover's target does; the button itself by default. */
  target?: (button: ReactElement) => ReactElement;
  /** A popover of this tool is open: the hover preview stays hidden. */
  open?: boolean;
  /** Live's slot budget is used up: the tool greys out (#233). */
  disabled?: boolean;
}

/**
 * A footer tool that chooses the source, as live's: an icon with its name as a tooltip. Once it
 * holds the source it turns active, with a round x badge on its corner that removes it and the
 * picture in a card above it on hover (live's composer shows no chip).
 */
export function SourceTool(props: SourceToolProps) {
  const { label, icon, view, removeLabel, onRemove, onClick, target, open = false } = props;
  const disabled = props.disabled === true;
  const chosen = view !== null;
  const button = (
    <ActionIcon
      variant={chosen ? "filled-light" : "subtle"}
      size={36}
      className={classes.toolButton}
      aria-label={chosen ? `${label}: ${view.name}` : label}
      disabled={disabled}
      onClick={onClick}
    >
      {icon}
    </ActionIcon>
  );
  return (
    <span className={classes.tool} data-chosen={chosen || undefined} data-open={open || undefined}>
      <Tooltip
        label={props.tip ?? label}
        withinPortal={false}
        {...(props.tip === undefined ? {} : { multiline: true, maw: 280 })}
      >
        {target === undefined ? button : target(button)}
      </Tooltip>
      {chosen ? (
        <>
          <UnstyledButton className={classes.toolBadge} aria-label={removeLabel} onClick={onRemove}>
            <X size={12} strokeWidth={2.5} aria-hidden />
          </UnstyledButton>
          {view.url === null ? null : (
            <span className={classes.toolPreview} aria-hidden>
              <img src={view.url} alt="" />
            </span>
          )}
        </>
      ) : null}
    </span>
  );
}
